import React, { useState, useMemo } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Bredcom from "../../components/Bredcom/Bredcom";
import Footer from "../../components/Footer/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import alumniTestimonials from "../../data/alumniTestimonials.json";

function getInitials(fullName = "") {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = (parts[parts.length - 1][0] || "").trim();
  return (first + last).toUpperCase();
}

/**
 * Avatar komponenta:
 * - ako ima src i ne pukne => slika
 * - ako nema src ili src pukne => inicijali
 */
function Avatar({ name, src, size = "md" }) {
  const [failed, setFailed] = useState(false);

  const initials = useMemo(() => getInitials(name), [name]);
  const hasImage =
    Boolean(src) && src !== "null" && src !== "undefined" && !failed;

  const sizeClass = size === "lg" ? "alumni-avatar--lg" : "";

  return (
    <div className={`alumni-avatar ${sizeClass}`} aria-label={name}>
      {hasImage ? (
        <img
          src={src}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </div>
  );
}

function AlumniKlub() {
  const { t, i18n } = useTranslation();

  const lang = (i18n.language || "me").toLowerCase().startsWith("en")
    ? "en"
    : "me";

  const testimonials = useMemo(() => {
    const list = alumniTestimonials?.[lang];
    return Array.isArray(list) ? list : [];
  }, [lang]);

  const presidentName =
    t("alumniPage.president.name") ||
    t("alumniPage.president.signature2") ||
    "-";

  const presidentRole =
    t("alumniPage.president.role") || t("alumniPage.president.heading") || "";

  const presidentPhoto = t("alumniPage.president.photo"); // može biti "" (fallback radi)

  return (
    <>
      <Navbar />

      <Bredcom title={t("common.home")} subtitle={t("alumni.title")} />

      <div className="container mt-4 pt-40 alumni-page">
        <div className="row">
          {/* MAIN CONTENT */}
          <div className="col-md-12">
            <div className="alumni-page__content">
              {/* =========================
                SEKCIJA A - HERO + NAV
              ========================== */}
              <section className="alumni-hero" aria-label="Alumni hero">
                <div className="alumni-hero__top">
                  <h2 className="alumni-hero__title">
                    {t("alumniPage.hero.title")}
                  </h2>
                  <p className="alumni-hero__subtitle">
                    {t("alumniPage.hero.subtitle")}
                  </p>

                  <div className="alumni-hero__cta">
                    <a className="btn alumni-btn" href="#utisci">
                      {t("alumniPage.hero.ctaPrimary")}
                      <i className="fas fa-quote-right" aria-hidden="true" />
                    </a>

                    <Link
                      className="btn alumni-btn alumni-btn--ghost"
                      to="/kontakt"
                    >
                      {t("alumniPage.hero.ctaSecondary")}
                      <i className="fas fa-envelope" aria-hidden="true" />
                    </Link>
                  </div>
                </div>

                <nav className="alumni-nav" aria-label="Alumni sekcije">
                  <a href="#o-alumni">{t("alumniPage.nav.about")}</a>
                  <a href="#predsjednica">{t("alumniPage.nav.president")}</a>
                  <a href="#utisci">{t("alumniPage.nav.testimonials")}</a>
                  <a href="#ukljucenje">{t("alumniPage.nav.join")}</a>
                  <a href="#saradnja">{t("alumniPage.nav.coop")}</a>
                  <a href="#pitanja">{t("alumniPage.nav.faq")}</a>
                </nav>
              </section>

              {/* =========================
                SEKCIJA B - O ALUMNI KLUBU (kraće + ciljevi)
              ========================== */}
              <section id="o-alumni" className="alumni-section">
                <header className="alumni-section__header">
                  <h3 className="alumni-section__title">
                    {t("alumniPage.about.title")}
                  </h3>
                  <p className="alumni-section__lead textjustify">
                    {t("alumni.p1")}
                  </p>
                </header>

                <div className="alumni-copy textjustify">
                  <p>{t("alumni.p2")}</p>
                  <p>{t("alumni.p3")}</p>
                </div>

                <div className="alumni-cards">
                  <div className="alumni-card">
                    <div className="alumni-card__icon" aria-hidden="true">
                      <i className="fas fa-link" />
                    </div>
                    <h4 className="alumni-card__title">
                      {t("alumniPage.goals.g1Title")}
                    </h4>
                    <p className="alumni-card__text">
                      {t("alumniPage.goals.g1Text")}
                    </p>
                  </div>

                  <div className="alumni-card">
                    <div className="alumni-card__icon" aria-hidden="true">
                      <i className="fas fa-handshake" />
                    </div>
                    <h4 className="alumni-card__title">
                      {t("alumniPage.goals.g2Title")}
                    </h4>
                    <p className="alumni-card__text">
                      {t("alumniPage.goals.g2Text")}
                    </p>
                  </div>

                  <div className="alumni-card">
                    <div className="alumni-card__icon" aria-hidden="true">
                      <i className="fas fa-briefcase" />
                    </div>
                    <h4 className="alumni-card__title">
                      {t("alumniPage.goals.g3Title")}
                    </h4>
                    <p className="alumni-card__text">
                      {t("alumniPage.goals.g3Text")}
                    </p>
                  </div>
                </div>
              </section>

              {/* =========================
                SEKCIJA C - RIJEČ PREDSJEDNICE (collapsible)
              ========================== */}
              <section id="predsjednica" className="alumni-section">
                <header className="alumni-section__header">
                  <h3 className="alumni-section__title">
                    {t("alumniPage.president.title")}
                  </h3>
                  <p className="alumni-section__lead">
                    {t("alumniPage.president.lead")}
                  </p>
                </header>

                {/* Vidljiva osoba (foto ili inicijali) + ime/uloga */}
                <div className="alumni-presidentCard">
                  <Avatar name={presidentName} src={presidentPhoto} size="lg" />
                  <div className="alumni-presidentCard__text">
                    <div className="alumni-presidentCard__name">
                      {presidentName}
                    </div>
                    {presidentRole ? (
                      <div className="alumni-presidentCard__role">
                        {presidentRole}
                      </div>
                    ) : null}
                  </div>
                </div>

                <details className="alumni-details">
                  <summary className="alumni-details__summary">
                    <span>{t("alumniPage.president.openLabel")}</span>
                    <span className="alumni-details__chev" aria-hidden="true">
                      ▾
                    </span>
                  </summary>

                  <div className="alumni-details__body">
                    <p>{t("alumniPage.president.p1")}</p>
                    <p>{t("alumniPage.president.p2")}</p>
                    <p>{t("alumniPage.president.p3")}</p>
                    <p>{t("alumniPage.president.p4")}</p>
                    <p>{t("alumniPage.president.p5")}</p>
                    <p className="alumni-signature">
                      <div>{t("alumniPage.president.signature1")}</div>
                      <div>{t("alumniPage.president.signature2")}</div>
                    </p>
                  </div>
                </details>
              </section>

              {/* =========================
                SEKCIJA D - UTISCI (kartice 1/2/3 responsive)
              ========================== */}
              <section id="utisci" className="alumni-section">
                <header className="alumni-section__header">
                  <h3 className="alumni-section__title">
                    {t("alumniPage.testimonials.title")}
                  </h3>
                  <p className="alumni-section__lead">
                    {t("alumniPage.testimonials.lead")}
                  </p>
                </header>
                {testimonials.length === 0 ? (
                  <div className="alumni-empty">
                    {t("alumniPage.testimonials.empty")}
                  </div>
                ) : (
                  <div className="alumni-testimonials">
                    {testimonials.map((item) => {
                      const initials = getInitials(item?.name);
                      const hasImage = Boolean(item?.image);

                      return (
                        <article className="alumni-tcard" key={item.id}>
                          <div className="alumni-tcard__top">
                            <h4 className="alumni-tcard__quoteTitle">
                              {item.quoteTitle}
                            </h4>
                            <p className="alumni-tcard__quote">{item.quote}</p>
                          </div>

                          <div className="alumni-tcard__footer">
                            <div className="alumni-avatar" aria-hidden="true">
                              {hasImage ? (
                                <img
                                  src={item.image}
                                  alt={item.name || ""}
                                  loading="lazy"
                                />
                              ) : (
                                <span className="alumni-avatar__initials">
                                  {initials}
                                </span>
                              )}
                            </div>

                            <div className="alumni-person">
                              <div className="alumni-person__name">
                                {item.name}
                              </div>

                              <div className="alumni-person__meta">
                                {item.level}
                                {item.program ? ` • ${item.program}` : ""}
                              </div>

                              {item.year && (
                                <div className="alumni-person__year">
                                  Generacija {item.year}
                                </div>
                              )}

                              {item.position && (
                                <div className="alumni-person__position">
                                  {item.position}
                                </div>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
                <div className="alumni-note textjustify">
                  <span className="alumni-note__badge">
                    {t("alumniPage.note.badge")}
                  </span>
                  <span>{t("alumniPage.note.text")}</span>
                </div>
              </section>

              {/* =========================
                SEKCIJA E - KAKO SE UKLJUČITI
              ========================== */}
              <section id="ukljucenje" className="alumni-section">
                <header className="alumni-section__header">
                  <h3 className="alumni-section__title">
                    {t("alumniPage.join.title")}
                  </h3>
                  <p className="alumni-section__lead">
                    {t("alumniPage.join.lead")}
                  </p>
                </header>

                <ol className="alumni-steps">
                  <li>{t("alumniPage.join.s1")}</li>
                  <li>{t("alumniPage.join.s2")}</li>
                  <li>{t("alumniPage.join.s3")}</li>
                </ol>

                {/* Novo: jasan checklist šta sve treba poslati */}
                <div className="alumni-req">
                  <h3 className="alumni-req__title">
                    {t("alumniPage.join.reqTitle")}
                  </h3>
                  <ul className="alumni-req__list">
                    {[1, 2, 3, 4, 5, 6, 7, 8]
                      .map((n) => t(`alumniPage.join.req${n}`))
                      .filter(Boolean)
                      .map((txt, idx) => (
                        <li key={idx}>{txt}</li>
                      ))}
                  </ul>
                </div>

                <div className="alumni-actions">
                  <a className="btn alumni-btn" href="#utisci">
                    {t("alumniPage.join.ctaPrimary")}
                    <i className="fas fa-quote-right" aria-hidden="true" />
                  </a>
                  <Link
                    className="btn alumni-btn alumni-btn--ghost"
                    to="/kontakt"
                  >
                    {t("alumniPage.join.ctaSecondary")}
                    <i className="fas fa-paper-plane" aria-hidden="true" />
                  </Link>
                </div>
              </section>

              {/* =========================
                SEKCIJA F - SARADNJA I PODRŠKA
              ========================== */}
              <section id="saradnja" className="alumni-section">
                <header className="alumni-section__header">
                  <h3 className="alumni-section__title">
                    {t("alumniPage.coop.title")}
                  </h3>
                  <p className="alumni-section__lead textjustify">
                    {t("alumniPage.coop.lead")}
                  </p>
                </header>

                <div className="alumni-copy textjustify">
                  <p>{t("alumni.p5")}</p>
                  <p>{t("alumni.p7")}</p>
                </div>

                <div className="alumni-highlight">
                  <div className="alumni-highlight__icon" aria-hidden="true">
                    <i className="fas fa-building" />
                  </div>
                  <div className="alumni-highlight__content">
                    <h4 className="alumni-highlight__title">
                      {t("alumniPage.coop.boxTitle")}
                    </h4>
                    <p className="alumni-highlight__text">
                      {t("alumniPage.coop.boxText")}
                    </p>
                    <Link
                      className="btn alumni-btn alumni-btn--ghost"
                      to="/kontakt"
                    >
                      {t("alumniPage.coop.cta")}
                      <i className="fas fa-arrow-right" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* =========================
                SEKCIJA G - FAQ
              ========================== */}
              <section
                id="pitanja"
                className="alumni-section alumni-section--last"
              >
                <header className="alumni-section__header">
                  <h3 className="alumni-section__title">
                    {t("alumniPage.faq.title")}
                  </h3>
                  <p className="alumni-section__lead">
                    {t("alumniPage.faq.lead")}
                  </p>
                </header>

                <div className="alumni-faq">
                  <details className="alumni-details">
                    <summary className="alumni-details__summary">
                      <span>{t("alumniPage.faq.q1")}</span>
                      <span className="alumni-details__chev" aria-hidden="true">
                        ▾
                      </span>
                    </summary>
                    <div className="alumni-details__body">
                      <p>{t("alumniPage.faq.a1")}</p>
                    </div>
                  </details>

                  <details className="alumni-details">
                    <summary className="alumni-details__summary">
                      <span>{t("alumniPage.faq.q2")}</span>
                      <span className="alumni-details__chev" aria-hidden="true">
                        ▾
                      </span>
                    </summary>
                    <div className="alumni-details__body">
                      <p>{t("alumniPage.faq.a2")}</p>
                    </div>
                  </details>

                  <details className="alumni-details">
                    <summary className="alumni-details__summary">
                      <span>{t("alumniPage.faq.q3")}</span>
                      <span className="alumni-details__chev" aria-hidden="true">
                        ▾
                      </span>
                    </summary>
                    <div className="alumni-details__body">
                      <p>{t("alumniPage.faq.a3")}</p>
                    </div>
                  </details>

                  <details className="alumni-details">
                    <summary className="alumni-details__summary">
                      <span>{t("alumniPage.faq.q4")}</span>
                      <span className="alumni-details__chev" aria-hidden="true">
                        ▾
                      </span>
                    </summary>
                    <div className="alumni-details__body">
                      <p>{t("alumniPage.faq.a4")}</p>
                    </div>
                  </details>

                  <details className="alumni-details">
                    <summary className="alumni-details__summary">
                      <span>{t("alumniPage.faq.q5")}</span>
                      <span className="alumni-details__chev" aria-hidden="true">
                        ▾
                      </span>
                    </summary>
                    <div className="alumni-details__body">
                      <p>{t("alumniPage.faq.a5")}</p>
                    </div>
                  </details>

                  <details className="alumni-details">
                    <summary className="alumni-details__summary">
                      <span>{t("alumniPage.faq.q6")}</span>
                      <span className="alumni-details__chev" aria-hidden="true">
                        ▾
                      </span>
                    </summary>
                    <div className="alumni-details__body">
                      <p>{t("alumniPage.faq.a6")}</p>
                    </div>
                  </details>
                </div>

                <div className="alumni-actions alumni-actions--tight">
                  <Link className="btn alumni-btn" to="/kontakt">
                    {t("alumniPage.faq.cta")}
                    <i className="fas fa-envelope" aria-hidden="true" />
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default AlumniKlub;
